import {
  createContext,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import Customer from "../types/customer";
import { Mediums } from "../types/mediums";
import Ticket from "../types/ticket";
import { ICustomerData } from "./signup/customer-info";
import CommandData from "../types/command-data";

const ACTIVE_FILE_STORAGE_KEY = "activeFileId";

export interface userData {
  name: string | null;
  code: number | null;
  isSupportUser: boolean;
}

export interface AuthContextProps {
  authId: string | null;
  activeFileId: number | null;
  setActiveFileId: (id: number | null) => void;
  tickets: Ticket[];
  setTickets: (tickets: Ticket[]) => void;
  customerInfo: Customer | null;
  setCustomerInfo: (customerInfo: Customer) => void;
  setCustomerData: (customerData: ICustomerData) => void;
  login: (authId: string) => void;
  logout: () => void;
  customerData: ICustomerData | undefined;
  serviceType: Mediums | undefined;
  setServiceType: (serviceType: Mediums) => void;
  setInternetCommands: (command: CommandData[]) => void;
  internetcommands: CommandData[];
  userData: userData | undefined;
  setUserData: (userdata: userData) => void;
  ngnSelectedProvince: number | null;
  setNgnSelectedProvince: (
    ngnSelectedProvince: number | null
  ) => void;
}

const AuthContext = createContext<AuthContextProps>({
  authId: null,
  serviceType: undefined,
  activeFileId: null,
  setActiveFileId: () => {},
  tickets: [],
  setTickets: () => {},
  customerInfo: null,
  customerData: undefined,
  setCustomerInfo: () => {},
  setCustomerData: () => {},
  login: () => {},
  logout: () => {},
  setServiceType: () => {},
  setInternetCommands: () => {},
  internetcommands: [],
  setUserData: () => {},
  userData: undefined,
  ngnSelectedProvince: null,
  setNgnSelectedProvince: () => {},
});

export default AuthContext;

export interface AuthContextProviderProps {
  children: ReactNode;
}

export const AuthContextProvider = ({
  children,
}: AuthContextProviderProps) => {
  const [params, setParams] = useSearchParams();

  const [authId, setAuthId] = useState<string | null>(() => {
    return (
      params.get("lId") ??
      localStorage.getItem("authId")
    );
  });

  const [activeFile, setActiveFile] = useState<number | null>(() => {
    const storedFileId = localStorage.getItem(
      ACTIVE_FILE_STORAGE_KEY
    );

    if (!storedFileId) {
      return null;
    }

    const fileId = Number(storedFileId);

    return Number.isNaN(fileId) ? null : fileId;
  });

  const [customerInfo, setCustomerInfo] =
    useState<Customer | null>(null);

  const [openTickets, setOpenTickets] = useState<Ticket[]>([]);

  const [customerData, setCustomerData] =
    useState<ICustomerData | undefined>();

  const [serviceType, setServiceType] =
    useState<Mediums | undefined>();

  const [ngnSelectedProvince, setNgnSelectedProvince] =
    useState<number | null>(null);

  const [internetcommands, setInternetCommands] = useState<
    CommandData[]
  >([]);

  const [userData, setUserData] = useState<userData>({
    name: null,
    code: null,
    isSupportUser: false,
  });

  const login = useCallback((newAuthId: string) => {
    localStorage.setItem("authId", newAuthId);
    setAuthId(newAuthId);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("authId");
    localStorage.removeItem(ACTIVE_FILE_STORAGE_KEY);

    setActiveFile(null);
    setAuthId(null);

    const newParams = new URLSearchParams(params);
    newParams.delete("lId");
    setParams(newParams);
  }, [params, setParams]);

  const setActiveFileId = useCallback(
    (id: number | null) => {
      if (id === null) {
        localStorage.removeItem(ACTIVE_FILE_STORAGE_KEY);
        setActiveFile(null);
        return;
      }

      localStorage.setItem(
        ACTIVE_FILE_STORAGE_KEY,
        String(id)
      );

      setActiveFile(id);
    },
    []
  );

  const context = useMemo<AuthContextProps>(
    () => ({
      authId,
      login,
      logout,

      activeFileId: activeFile,
      setActiveFileId,

      tickets: openTickets,
      setTickets: setOpenTickets,

      customerInfo,
      setCustomerInfo,

      customerData,
      setCustomerData,

      setServiceType,
      serviceType,

      setUserData,
      userData,

      ngnSelectedProvince,
      setNgnSelectedProvince,

      internetcommands,
      setInternetCommands,
    }),
    [
      authId,
      login,
      logout,
      activeFile,
      setActiveFileId,
      openTickets,
      customerInfo,
      customerData,
      serviceType,
      userData,
      ngnSelectedProvince,
      internetcommands,
    ]
  );

  return (
    <AuthContext.Provider value={context}>
      {children}
    </AuthContext.Provider>
  );
};
